import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAuctions } from "../../redux/slices/auctionsSlice";
import { useTranslation } from "../../contexts/LanguageContext";

export default function Auctions() {
  const dispatch = useDispatch();
  const { auctions, loading, error } = useSelector((state) => state.auctions);
  const { t } = useTranslation();

  // Fetch recent auctions for dashboard
  useEffect(() => {
    dispatch(fetchAuctions({
      currentPage: 1,
      limit: 5, // Show only 5 recent auctions on dashboard
      sortBy: 'createdAt',
      sortOrder: 'desc'
    }));
  }, [dispatch]);

  // Format date and time for display
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString() + " | " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 bg-white rounded-lg overflow-hidden">
      <div className="sm:flex sm:items-center py-4">
        <div className="sm:flex-auto">
          <h3 className="font-medium font-base">{t("dashboard.recentAuctions")}</h3>
        </div>
      </div>

      <div className="flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle">
            <div className="overflow-hidden shadow-sm ring-1 ring-black ring-opacity-5">
              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-sm">{t("dashboard.loadingAuctions")}</span>
                </div>
              ) : error ? (
                <div className="text-center py-8">
                  <p className="text-red-500 text-sm">{error}</p>
                </div>
              ) : auctions.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">{t("dashboard.noRecentAuctions")}</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-light text-themeSemiGray sm:pl-6 lg:pl-8">
                        {t("dashboard.col.id")}
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-light text-themeSemiGray">
                        {t("dashboard.col.title")}
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-light text-themeSemiGray">
                        {t("dashboard.col.creator")}
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-light text-themeSemiGray">
                        {t("dashboard.col.duration")}
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-light text-themeSemiGray">
                        {t("dashboard.col.lots")}
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-light text-themeSemiGray">
                        {t("dashboard.col.totalBids")}
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-light text-themeSemiGray">
                        {t("dashboard.col.type")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-300 bg-white">
                    {auctions.slice(0, 5).map((auction) => (
                      <tr key={auction._id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-themeGray sm:pl-6 lg:pl-8">
                          {auction._id?.slice(-6) || 'N/A'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-themeGray font-medium">
                          <div className="max-w-32 truncate" title={auction.title}>
                            {auction.title || t("dashboard.untitledAuction")}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-themeGray font-medium">
                          {auction.seller?.name || auction.userId?.name || t("dashboard.unknown")}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-themeGray font-medium">
                          <div>{formatDateTime(auction.startTime)}</div>
                          <div>{formatDateTime(auction.endTime)}</div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-themeGray font-medium">
                          {auction.lotIds?.length || auction.lots?.length || 0}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-themeGray font-medium">
                          {auction.totalBids || 0}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-themeGray font-medium">
                          {auction.bidderType || 'Standard'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
